// Hoe het werkt in drie stappen (#347, module site). Gebundeld naar /js/site/stappen.js.
// Geladen op /hoe-het-werkt (alleen in testmodus) en op /nieuwe-test-1. De HTML komt van
// stappenAnimatieHtml() in nieuwe-test-1.ts, de opmaak van nieuwe-test-1/stappen.css.
//
// De server zet beide voorbeeldgesprekken helemaal in de HTML. Zonder JavaScript en bij
// prefers-reduced-motion blijft dat zo staan: alles direct zichtbaar, niets typt. Anders haalt dit
// script de berichten weg en speelt het ze af zodra een chat in beeld komt: jouw bericht wordt in de
// invoerbalk getypt en verstuurd, de AI "typt" (drie stippen) en schrijft woord voor woord, en een
// aanroep van de tool gaat van bezig naar klaar. Bij stap 1 komt daarbij de toestemmingspop-up, met
// een klik op Toestaan. Naast elkaar (vanaf 900 px) spelen de chats om de beurt; op een smal scherm
// speelt elke chat als hij zelf in beeld komt.
//
// "Bekijk de bouwtekening" in stap 3 hoort niet bij dit script: browser/bouwtekening.ts opent daarmee
// de hele bouw-PDF in een viewer (SITE3). Het model blijft sleepbaar; het draait vanzelf via
// data-autodraai (module 3d), en een klik op een onderdeel toont de kaart van dat onderdeel.
//
// Afspraak met de HTML: [data-stappen-anim] > .sa-rij > li > .chatvenster[data-chat] >
// .chat-berichten > (.msg.u | .msg.a | .kaart-tool[data-bezig][data-popup] > .status), .chat-invoer,
// .popup[hidden] > .toestaan.

const minderBeweging = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const naastElkaar = window.matchMedia("(min-width: 900px)").matches;

function wacht(ms: number): Promise<void> {
  return new Promise((klaar) => setTimeout(klaar, ms));
}

function maak(tag: string, klasse: string, tekst = ""): HTMLElement {
  const el = document.createElement(tag);
  el.className = klasse;
  el.textContent = tekst;
  return el;
}

class Gesprek {
  private readonly berichten: HTMLElement;
  private readonly invoer: HTMLElement | null;
  private readonly popup: HTMLElement | null;
  private readonly stappen: HTMLElement[];
  private bezig: Promise<void> | null = null;

  constructor(readonly venster: HTMLElement) {
    this.berichten = venster.querySelector<HTMLElement>(".chat-berichten") ?? venster;
    this.invoer = venster.querySelector<HTMLElement>(".chat-invoer");
    this.popup = venster.querySelector<HTMLElement>(".popup");
    // De berichten van de server zijn het script van het gesprek. Ze gaan weg en komen één voor één terug.
    this.stappen = [...this.berichten.children].filter((el): el is HTMLElement => el instanceof HTMLElement);
    for (const el of this.stappen) el.remove();
  }

  speel(): Promise<void> {
    this.bezig ??= this.speelAf();
    return this.bezig;
  }

  private volg() {
    this.berichten.scrollTop = this.berichten.scrollHeight;
  }

  private async speelAf() {
    for (const stap of this.stappen) {
      if (stap.classList.contains("kaart-tool")) await this.tool(stap);
      else if (stap.classList.contains("u")) await this.jij(stap);
      else await this.ai(stap);
    }
  }

  /** Jouw bericht: getypt in de invoerbalk, daarna verstuurd. */
  private async jij(stap: HTMLElement) {
    const tekst = stap.textContent ?? "";
    await wacht(600);
    if (this.invoer) {
      for (const teken of tekst) {
        this.invoer.textContent += teken;
        await wacht(38);
      }
      await wacht(450);
      this.invoer.textContent = "";
    }
    this.berichten.append(stap);
    this.volg();
    await wacht(300);
  }

  /** De AI: drie stippen, dan woord voor woord. */
  private async ai(stap: HTMLElement) {
    // Een link (zoals "Bekijk het 3D-model") komt na het typen in zijn geheel terug.
    const link = stap.querySelector("a");
    link?.remove();
    const tekst = (stap.textContent ?? "").trim();
    const typ = maak("div", "msg a typ");
    typ.append(maak("span", ""), maak("span", ""), maak("span", ""));
    this.berichten.append(typ);
    this.volg();
    await wacht(900);
    typ.remove();
    stap.textContent = "";
    this.berichten.append(stap);
    const woorden = tekst.split(" ");
    for (let n = 0; n < woorden.length; n += 1) {
      stap.textContent += (n ? " " : "") + woorden[n];
      this.volg();
      await wacht(70);
    }
    if (link) {
      stap.append(" ", link);
      this.volg();
    }
  }

  /** Een aanroep van de tool: eerst bezig, bij stap 1 met de toestemmingspop-up, dan klaar. */
  private async tool(stap: HTMLElement) {
    const status = stap.querySelector<HTMLElement>(".status");
    const klaar = status?.textContent ?? "";
    if (status) {
      status.textContent = stap.dataset.bezig ?? "";
      status.classList.remove("klaar");
    }
    await wacht(400);
    this.berichten.append(stap);
    this.volg();
    await wacht(900);
    if (stap.dataset.popup && this.popup) {
      const toestaan = this.popup.querySelector<HTMLElement>(".toestaan");
      this.popup.hidden = false;
      await wacht(2200);
      toestaan?.classList.add("ingedrukt");
      await wacht(600);
      this.popup.hidden = true;
      toestaan?.classList.remove("ingedrukt");
    } else {
      await wacht(1200);
    }
    if (status) {
      status.textContent = klaar;
      status.classList.add("klaar");
    }
  }
}

function startGesprekken(blok: HTMLElement) {
  const gesprekken = [...blok.querySelectorAll<HTMLElement>(".chatvenster[data-chat]")].map((v) => new Gesprek(v));
  blok.dataset.js = "1";
  if (!gesprekken.length) return;

  let omDeBeurt: Promise<void> | null = null;
  const speelOmDeBeurt = () =>
    (omDeBeurt ??= (async () => {
      for (const g of gesprekken) await g.speel();
    })());

  const kijker = new IntersectionObserver(
    (items) => {
      for (const item of items) {
        if (!item.isIntersecting) continue;
        const gesprek = gesprekken.find((g) => g.venster === item.target);
        if (naastElkaar) void speelOmDeBeurt();
        else if (gesprek) void gesprek.speel();
        kijker.unobserve(item.target);
      }
    },
    { threshold: 0.4 },
  );
  for (const g of gesprekken) kijker.observe(g.venster);
}

for (const blok of document.querySelectorAll<HTMLElement>("[data-stappen-anim]")) {
  // Minder beweging of een oude browser: de gesprekken blijven staan zoals de server ze zette.
  if (!minderBeweging && "IntersectionObserver" in window) startGesprekken(blok);
}

// Eigen scope (module), zodat namen niet botsen met andere browserbestanden in de typecheck.
export {};
